/**
 * ScriptEdit Page - MA-ARCH-165 Refactored
 * 
 * Script Builder interface for creating and editing conversation scripts.
 * 
 * V2.0 (MA-ARCH-165): Major refactor - integrated extracted hooks
 *   - useScriptEditorState: Core editor state (graph, node selection, editing)
 *   - useScriptActions: Save/add/delete actions for nodes and options
 *   - useDataFields: Form field management
 *   - useQuoteFields: Quote sheet field management
 *   - useTokenSearch: Token search and filtering
 * 
 * V1.5 (MA-ARCH-163): Extracted utilities and components
 * V1.0: Original monolithic implementation
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getStoredUser } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Download,
  Play,
  ChevronDown,
  Pencil,
  ArrowUp,
  ArrowDown,
  Sigma,
  List,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
  MessageSquareText,
  User,
  Star,
  FileText,
} from "lucide-react";
import NodeListReorder from "@/components/NodeListReorder";
import { getQuoteFieldLabel, isCompanyQuoteField, isIssueAgeQuoteField, isProductQuoteField } from "@/lib/quoteFieldUtils";

// Extracted utilities (MA-ARCH-163)
import {
  getNodeLabel,
  getNodePreviewText,
  QUOTE_FIELD_TYPE_LABELS,
  DATA_FIELD_TYPE_LABELS,
  quoteTokenToText,
  renderQuoteFormula,
  QUOTE_LABEL_PRESETS,
} from "./script-edit/utils/scriptEditUtils";

// Extracted components (MA-ARCH-163)
import { ProspectResponsesList } from "./script-edit/components/ProspectResponsesList";

// Extracted hooks (MA-ARCH-163, integrated in MA-ARCH-165)
import {
  useScriptEditorState,
  useScriptActions,
  useDataFields,
  useQuoteFields,
  useTokenSearch,
} from "./script-edit/hooks";

export default function ScriptEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getStoredUser();
  const isAgentRole = ["agent", "booking_agent"].includes(user?.role);
  const canEdit = user?.role === "super_admin" || user?.role === "admin";
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  // Hard redirect agents away from Builder (P0 requirement)
  useEffect(() => {
    if (isAgentRole) {
      navigate(`/app/scripts/${id}/play`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.role]);

  // ============================================================================
  // INTEGRATED HOOKS (MA-ARCH-165)
  // ============================================================================

  // Core editor state from useScriptEditorState
  const editorState = useScriptEditorState(id, canEdit);
  const {
    graph,
    loading,
    activeNodeId,
    setActiveNodeId,
    scriptName,
    setScriptName,
    isRenaming,
    nodeTitle,
    setNodeTitle,
    yourReply,
    setYourReply,
    scriptBlocks,
    setScriptBlocks,
    coachingNote,
    setCoachingNote,
    nodeScope,
    setNodeScope,
    localNodes,
    setLocalNodes,
    isNodeOrderDirty,
    setIsNodeOrderDirty,
    textAreaRefs,
    active,
    isStart,
    isGreeting,
    nodesList,
    orderedNodesList,
    nodePositionById,
    fetchGraph,
    insertTokenIntoTextBlock,
    autoSaveDraftIfDirty,
  } = editorState;

  // Script actions from useScriptActions
  const scriptActions = useScriptActions(id, editorState, canEdit);
  const {
    onSaveNode,
    onAddNode,
    onDeleteNode,
    onSetStart,
    onSaveScriptName,
    saveNodeOrder,
    onUpdateCollectProspectInfoAt,
    onAddOption,
    onUpdateOption,
    onDeleteOption,
  } = scriptActions;

  // Data fields from useDataFields
  const dataFieldsHook = useDataFields(id, activeNodeId, autoSaveDraftIfDirty, canEdit);
  const {
    formFields,
    dataFieldSearch,
    setDataFieldSearch,
    dataFieldOptions,
    activeNodeFields,
    addFieldOpen,
    setAddFieldOpen,
    newFieldLabel,
    setNewFieldLabel,
    newFieldType,
    setNewFieldType,
    newFieldRequired,
    setNewFieldRequired,
    newFieldScope,
    setNewFieldScope,
    newFieldOptions,
    setNewFieldOptions,
    editFieldOpen,
    setEditFieldOpen,
    editFieldId,
    editFieldLabel,
    setEditFieldLabel,
    editFieldType,
    setEditFieldType,
    editFieldRequired,
    setEditFieldRequired,
    editFieldScope,
    setEditFieldScope,
    editFieldOptions,
    setEditFieldOptions,
    fetchFormFields,
    onAddField,
    openEditField,
    onUpdateField,
    onDeleteField,
  } = dataFieldsHook;

  // Quote fields from useQuoteFields
  const quoteFieldsHook = useQuoteFields(id, autoSaveDraftIfDirty, canEdit);
  const {
    quoteFields,
    quoteTab,
    setQuoteTab,
    quoteTemplates,
    quoteTemplateId,
    setQuoteTemplateId,
    quoteTemplateLoading,
    quoteTemplateApplying,
    requiredQuoteFieldIds,
    quoteLabelMode,
    setQuoteLabelMode,
    quoteNewPresetLabel,
    setQuoteNewPresetLabel,
    quoteNewCustomLabel,
    setQuoteNewCustomLabel,
    quoteNewFieldType,
    setQuoteNewFieldType,
    quoteNewOptions,
    setQuoteNewOptions,
    quoteNewIncludeInHousehold,
    setQuoteNewIncludeInHousehold,
    editQuoteOpen,
    setEditQuoteOpen,
    editQuoteField,
    editQuoteLabel,
    setEditQuoteLabel,
    editQuoteType,
    setEditQuoteType,
    editQuoteOptions,
    setEditQuoteOptions,
    editQuoteIncludeInHousehold,
    setEditQuoteIncludeInHousehold,
    formulaOpen,
    setFormulaOpen,
    formulaField,
    formulaTokens,
    setFormulaTokens,
    formulaInsertValue,
    setFormulaInsertValue,
    formulaNumberDraft,
    setFormulaNumberDraft,
    fetchQuoteFields,
    onAddQuoteField,
    onDeleteQuoteField,
    onUpdateQuoteField,
    onMoveQuoteField,
    openEditInputField,
    openEditCalculatedField,
    onSaveEditQuote,
    openFormulaEditor,
    addFormulaToken,
    removeLastFormulaToken,
    onSaveFormula,
    onApplyQuoteTemplate,
  } = quoteFieldsHook;

  // Token search from useTokenSearch
  const tokenSearchHook = useTokenSearch(formFields, quoteFields);
  const {
    tokenSearch,
    setTokenSearch,
    tokenDataFields,
    tokenQuoteInputs,
    tokenQuoteCalculated,
    tokenQuoteOptions,
    tokenAgentOptions,
    tokenCustomerOptions,
    hasTokenMatches,
  } = tokenSearchHook;

  // ============================================================================
  // LOCAL UI STATE (not extracted to hooks - view-specific)
  // ============================================================================

  // Builder view toggle
  const [builderView, setBuilderView] = useState("node"); // node | quote

  // Runner start config
  const [runSandbox, setRunSandbox] = useState(false);

  // Add node dialog state
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [newNodeTitle, setNewNodeTitle] = useState("");
  const [newNodeReply, setNewNodeReply] = useState("");

  // Add option dialog state
  const [addOptionOpen, setAddOptionOpen] = useState(false);
  const [optProspectSays, setOptProspectSays] = useState("");
  const [optTargetId, setOptTargetId] = useState("");

  // ============================================================================
  // HANDLERS (thin wrappers that use hooks)
  // ============================================================================

  // Handle adding a node via dialog
  const handleAddNode = useCallback(async () => {
    const result = await onAddNode(newNodeTitle, newNodeReply);
    if (result?.success) {
      setAddNodeOpen(false);
      setNewNodeTitle("");
      setNewNodeReply("");
    }
  }, [onAddNode, newNodeTitle, newNodeReply]);

  // Handle adding an option via dialog
  const handleAddOption = useCallback(async () => {
    const result = await onAddOption(optProspectSays, optTargetId);
    if (result?.success) {
      setAddOptionOpen(false);
      setOptProspectSays("");
      setOptTargetId("");
    }
  }, [onAddOption, optProspectSays, optTargetId]);

  // Handle saving edit field (wrap hook's onUpdateField)
  const onSaveEditField = useCallback(async () => {
    if (!editFieldId) return;

    const patch = {
      label: editFieldLabel.trim(),
      field_type: editFieldType,
      required: editFieldRequired,
      scope: editFieldScope,
    };

    if (editFieldType === "dropdown") {
      patch.options = editFieldOptions
        .split(",")
        .map((opt) => opt.trim())
        .filter(Boolean);
    } else {
      patch.options = [];
    }

    await onUpdateField(editFieldId, patch);
    toast({ title: "Field updated" });
    setEditFieldOpen(false);
  }, [editFieldId, editFieldLabel, editFieldType, editFieldRequired, editFieldScope, editFieldOptions, onUpdateField, setEditFieldOpen]);

  // Handle moving data field
  const onMoveField = useCallback(async (field, direction) => {
    await autoSaveDraftIfDirty();

    const sorted = [...activeNodeFields].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const idx = sorted.findIndex((f) => f.id === field.id);
    const swapWith = idx + direction;
    if (idx === -1 || swapWith < 0 || swapWith >= sorted.length) return;

    const a = sorted[idx];
    const b = sorted[swapWith];

    await Promise.all(
      sorted.map((f, i) => (f.sort_order === i ? Promise.resolve() : onUpdateField(f.id, { sort_order: i })))
    );

    await onUpdateField(a.id, { sort_order: swapWith });
    await onUpdateField(b.id, { sort_order: idx });
  }, [autoSaveDraftIfDirty, activeNodeFields, onUpdateField]);

  // Handle saving quote field edits (wrapper for dialog)
  const onSaveQuoteFieldEdits = useCallback(async () => {
    if (!editQuoteField?.id) return;

    const patch = {
      label: (editQuoteLabel || "").trim(),
      field_type: editQuoteType,
    };

    if (editQuoteType === "dropdown") {
      patch.options = (editQuoteOptions || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    } else {
      patch.options = [];
    }

    if (editQuoteType === "number") {
      patch.include_in_household = !!editQuoteIncludeInHousehold;
    } else {
      patch.include_in_household = false;
    }

    patch.kind = editQuoteField.kind;

    await onUpdateQuoteField(editQuoteField.id, patch);
    toast({ title: "Quote field updated" });
    setEditQuoteOpen(false);
  }, [editQuoteField, editQuoteLabel, editQuoteType, editQuoteOptions, editQuoteIncludeInHousehold, onUpdateQuoteField, setEditQuoteOpen]);

  // Open quote edit dialog for input fields
  const openEditQuoteField = useCallback((field) => {
    openEditInputField(field);
  }, [openEditInputField]);

  // Export handler
  const onExport = useCallback(async () => {
    try {
      const res = await api.get(`/scripts/${id}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${graph?.script?.name || "script"}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Exported" });
    } catch (e) {
      toast({
        title: "Export failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [id, graph?.script?.name]);

  // ============================================================================
  // LEGACY ALIASES (for JSX compatibility)
  // ============================================================================

  const nodeLabel = getNodeLabel;
  const nodePreviewText = getNodePreviewText;

  const fieldTypeLabels = {
    text: "Text",
    phone: "Phone",
    email: "Email",
    textarea: "Long text",
    checkbox: "Checkbox",
    dropdown: "Dropdown",
  };

  const quoteFieldTypeLabels = {
    text: "Text",
    textarea: "Long text",
    dropdown: "Drop down",
    number: "Number",
  };


  return (
    <div className="space-y-5" data-testid="script-edit-page">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="hidden lg:block lg:w-0" aria-hidden />
        <div className="order-1 flex items-center gap-3 lg:flex-1 lg:min-w-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/app"))}
                  data-testid="back-to-dashboard-button"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Back</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div>
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <Input
                  value={scriptName}
                  onChange={(e) => setScriptName(e.target.value)}
                  disabled={!canEdit || isRenaming}
                  className="h-10 max-w-[520px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                  placeholder="Script title"
                  data-testid="script-title-input"
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        disabled={!canEdit || isRenaming || !scriptName.trim()}
                        onClick={onSaveScriptName}
                        data-testid="script-title-save-button"
                      >
                        <Save className="h-4 w-4" />
                     </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Save title</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {graph?.script?.script_type ? (
                <Badge
                  variant="outline"
                  className="text-slate-200/60"
                  data-testid="script-type-badge"
                >
                  {graph.script.script_type === "appointment"
                    ? "Appointment"
                    : "Sales"}
                </Badge>
              ) : null}
            </div>
            {/* subtitle removed per design */}
          </div>
        </div>

        <div className="order-2 flex flex-wrap items-center gap-2 lg:flex-none" data-testid="script-edit-actions">
          {/* Node / Quote Sheet selector - TabsList style matching Admin Console */}
          <Tabs value={builderView} onValueChange={setBuilderView}>
            <TabsList
              className="inline-flex h-auto w-fit gap-1 bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20"
              data-testid="builder-header-view-toggle"
            >
              <TabsTrigger
                value="node"
                className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
                data-testid="builder-view-node"
              >
                Node
              </TabsTrigger>
              <TabsTrigger
                value="quote"
                className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
                data-testid="builder-view-quote"
              >
                Quote Sheet
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onExport}
                  disabled={!canEdit}
                  data-testid="export-script-button"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>


          <div className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-2 ring-1 ring-white/10">
            <span className="text-xs text-slate-200/80">Prospect Info</span>
            <Select
              value={graph?.script?.collect_prospect_info_at || "start"}
              onValueChange={(v) => onUpdateCollectProspectInfoAt(v)}
              disabled={!canEdit}
            >
              <SelectTrigger className="h-8 w-[170px] rounded-full border-white/10 bg-white/5 text-slate-100">
                <SelectValue placeholder="At Start" />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                <SelectItem value="start">At Start</SelectItem>
                <SelectItem value="disposition">At Disposition</SelectItem>
              </SelectContent>
            </Select>
          </div>

              <TooltipContent side="bottom">Export</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {isAdmin ? (
            <div
              className={
                "flex items-center gap-2 rounded-full px-3 py-2 ring-1 " +
                (runSandbox ? "bg-amber-500/20 ring-amber-400/30" : "bg-white/5 ring-white/10")
              }
            >
              <Switch checked={runSandbox} onCheckedChange={setRunSandbox} variant="sandbox" />
              <span className={"text-xs " + (runSandbox ? "text-amber-100" : "text-slate-200/80")}>
                Sandbox
              </span>
            </div>
          ) : null}

          <Button
            asChild
            data-testid="go-to-runner-button"
          >
            <Link to={`/app/scripts/${id}/play${runSandbox ? "?sandbox=true" : ""}`}>
              <Play className="mr-2 h-4 w-4" />
              {runSandbox ? "Run (Sandbox)" : "Run"}
            </Link>
          </Button>
        </div>
      </div>

      {loading && <div className="muted" data-testid="script-edit-loading-text">Loading...</div>}
      {!loading && !graph && <div className="muted" data-testid="script-edit-missing-text">Script not found.</div>}
      {!loading && graph && (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12" data-testid="script-edit-grid">
          {/* Left: node list */}
          <Card className="glass lg:col-span-4" data-testid="node-list-card">
              <CardHeader className="space-y-1">
              <div className="flex flex-row items-center justify-between">
                <CardTitle className="text-slate-100" data-testid="node-list-title">
                  Nodes
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Dialog open={addNodeOpen} onOpenChange={setAddNodeOpen}>
                    <DialogTrigger asChild>
                      <Button
                        size="icon"
                        disabled={!canEdit}
                        data-testid="add-node-open-button"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>

                    <DialogContent className="glass border-white/10" data-testid="add-node-dialog">
                      <DialogHeader>
                        <DialogTitle className="text-slate-100" data-testid="add-node-title">
                          Add a node
                        </DialogTitle>
                        <DialogDescription className="text-slate-200/70" data-testid="add-node-description">
                          A node is what you say next. You’ll connect nodes using prospect responses.
                        </DialogDescription>
                      </DialogHeader>


                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm text-slate-200/80" data-testid="add-node-title-label">
                          Title (optional)
                        </label>
                        <Input
                          value={newNodeTitle}
                          onChange={(e) => setNewNodeTitle(e.target.value)}
                          placeholder="Handle price objection"
                          className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                          data-testid="add-node-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-slate-200/80" data-testid="add-node-reply-label">
                          Your reply
                        </label>
                        <Textarea
                          value={newNodeReply}
                          onChange={(e) => setNewNodeReply(e.target.value)}
                          placeholder="Write the exact line you want to read"
                          className="min-h-[120px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                          data-testid="add-node-reply-input"
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        onClick={onAddNode}
                        data-testid="add-node-submit-button"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {isNodeOrderDirty ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            onClick={saveNodeOrder}
                            disabled={!canEdit}
                            data-testid="save-node-order-button"
                            title="Save node order"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">Save order</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : null}
                </div>
              </div>
              <div className="text-sm text-slate-200/70" data-testid="node-list-reorder-hint">
                Drag nodes to reorder. Click Save to persist changes.
              </div>
              <div className="muted text-xs" data-testid="node-list-hint">
                Tip: Keep node titles short. Use “Set Start” to pick the first line.
              </div>
            </CardHeader>

            <CardContent className="space-y-2">
              <div className="max-h-[520px] overflow-auto pr-1" data-testid="node-list-scroll">
                <NodeListReorder
                  items={localNodes || nodesList}
                  getId={(wrap) => wrap.node.id}
                  disabled={!canEdit}
                  onReorder={(next) => {
                    setLocalNodes(next);
                    setIsNodeOrderDirty(true);
                  }}
                >
                  {(wrap, idx) => {
                    const n = wrap.node;
                    const selected = n.id === activeNodeId;
                    const start = graph.script.start_node_id === n.id;
                    const greeting =
                      graph?.script?.greeting_node_id === n.id ||
                      n.node_type === "greeting";
                    const pos = idx + 1;

                    return (
                      <div
                        className={
                          "w-full rounded-xl border px-3 py-3 ring-1 transition-colors " +
                          (selected
                            ? "border-indigo-400/30 bg-indigo-500/10 ring-indigo-400/20"
                            : "border-white/10 bg-white/5 ring-white/10 hover:bg-white/10")
                        }
                        data-testid={`node-list-item-${n.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <button
                            className="flex-1 text-left"
                            onClick={async () => {
                              try {
                                await autoSaveDraftIfDirty();
                              } finally {
                                setBuilderView("node");
                                setActiveNodeId(n.id);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="flex h-6 w-6 items-center justify-center rounded-md bg-white/10 text-xs text-slate-100 ring-1 ring-white/10"
                                data-testid={`node-list-item-position-${n.id}`}
                              >
                                {pos}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div
                                  className="truncate text-sm font-medium text-slate-100"
                                  data-testid={`node-list-item-title-${n.id}`}
                                >
                                  {nodeLabel(n)}
                                </div>
                                <div
                                  className="mt-1 line-clamp-2 text-xs text-slate-200/65"
                                  data-testid={`node-list-item-snippet-${n.id}`}
                                >
                                  {nodePreviewText(n)}
                                </div>
                              </div>

                              {greeting ? (
                                <Badge className="bg-emerald-500/15 text-emerald-100 ring-1 ring-emerald-400/20">
                                  Greeting
                                </Badge>
                              ) : null}

                              {start ? (
                                <Badge
                                  className="bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/20"
                                  data-testid={`node-list-item-start-badge-${n.id}`}
                                >
                                  Start
                                </Badge>
                              ) : null}
                            </div>
                          </button>
                        </div>
                      </div>
                    );
                  }}
                </NodeListReorder>
              </div>
            </CardContent>
          </Card>



          {/* Quote Sheet Card (separate) */}
          {builderView === "quote" ? (
            <div className="lg:col-span-8">
              

              <Card className="glass" data-testid="quote-sheet-card">
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-slate-100" data-testid="quote-sheet-card-title">
                        <List className="h-5 w-5 text-indigo-400" />
                        Quote Sheet
                      </CardTitle>
                      <div className="mt-2 text-sm text-slate-200/70" data-testid="quote-sheet-card-subtitle">
                        Configure quote inputs and calculated fields for the Tool Bar.
                      </div>
                    </div>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            className={
                              "rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500 " +
                              (!canEdit ? "opacity-60" : "")
                            }
                            disabled={!canEdit}
                            onClick={onAddQuoteField}
                            data-testid="quote-sheet-add-field"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">Add</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </CardHeader>


                {!canEdit ? (
                  <div
                    className="border-b border-white/10 bg-amber-500/10 px-6 py-3 text-sm text-amber-100"
                    data-testid="quote-sheet-readonly-banner"
                  >
                    Read-only mode: your role can run this script, but cannot edit it.
                  </div>
                ) : null}

              {/* Quote sheet header rendered above (deduped) */}

              <CardContent className="space-y-4">
                {/* Edit Quote Field Dialog */}
                <Dialog open={editQuoteOpen} onOpenChange={setEditQuoteOpen}>
                  <DialogContent className="glass border-white/10" data-testid="edit-quote-field-dialog">
                    <DialogHeader>
                      <DialogTitle className="text-slate-100">Edit quote field</DialogTitle>
                      <DialogDescription className="text-slate-200/70">
                        Update the label, type, options, and household behavior.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm text-slate-200/80">Label</label>
                        <Input
                          value={editQuoteLabel}
                          onChange={(e) => setEditQuoteLabel(e.target.value)}
                          className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm text-slate-200/80">Type</label>
                        <Select
                          value={editQuoteType}
                          onValueChange={(v) => {
                            setEditQuoteType(v);
                            if (v !== "dropdown") setEditQuoteOptions("");
                            if (v !== "number") setEditQuoteIncludeInHousehold(false);
                          }}
                        >
                          <SelectTrigger className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10">
                            <SelectValue placeholder="Choose type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="textarea">Long text</SelectItem>
                            <SelectItem value="dropdown">Drop down</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {editQuoteType === "dropdown" ? (
                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80">Options (comma-separated)</label>
                          <Input
                            value={editQuoteOptions}
                            onChange={(e) => setEditQuoteOptions(e.target.value)}
                            
                            className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10"
                          />
                        </div>
                      ) : null}

                      {editQuoteType === "number" ? (
                        <div className="flex items-center gap-3">
                          <Switch checked={editQuoteIncludeInHousehold} onCheckedChange={setEditQuoteIncludeInHousehold} />

                          <div className="text-sm text-slate-200/80">Include in Household sums</div>
                        </div>
                      ) : null}
                    </div>

                    <DialogFooter>
                    <Button
                        variant="secondary"
                        className="rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                        onClick={() => setEditQuoteOpen(false)}
                      >
                        Cancel
                     </Button>
                    <Button
                        className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                        onClick={onSaveQuoteFieldEdits}
                        disabled={!canEdit || !editQuoteLabel.trim()}
                      >
                        Save
                     </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Formula Editor Dialog (Calculated fields) */}
                <Dialog open={formulaOpen} onOpenChange={setFormulaOpen}>
                  <DialogContent className="glass border-white/10" data-testid="quote-formula-dialog">
                    <DialogHeader>
                      <DialogTitle className="text-slate-100">Edit formula</DialogTitle>
                      <DialogDescription className="text-slate-200/70">
                        Build formulas using field tokens like <span className="font-mono">{'{Monthly Premium}'}</span>.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="text-sm text-slate-200/80">Preview</div>
                        <div className="rounded-xl border border-white/10 bg-black/30 p-3 font-mono text-sm text-slate-100">
                          {renderQuoteFormula(formulaTokens) || "(empty)"}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="space-y-2">
                          <div className="text-sm text-slate-200/80">Insert</div>
                          <Select
                            value={formulaInsertValue}
                            onValueChange={(v) => {
                              setFormulaInsertValue(v);
                              if (!v) return;

                              // value format:
                              // - field: source::id::label
                              // - sum-all: sum::source::id::label
                              const parts = v.split("::");
                              if (parts[0] === "sum") {
                                const [, source, refId, ...rest] = parts;
                                const label = rest.join("::");
                                addFormulaToken({ type: "sum_all", source, id: refId, label });
                                setFormulaInsertValue("");
                                return;
                              }

                              const [source, refId, ...rest] = parts;
                              const label = rest.join("::");
                              addFormulaToken({ type: "field", source, id: refId, label });
                              setFormulaInsertValue("");
                            }}
                          >
                            <SelectTrigger className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10">
                              <SelectValue placeholder="Insert field…" />
                            </SelectTrigger>
                            <SelectContent>
                              <div className="px-2 py-1 text-xs text-slate-200/60">Quote inputs</div>
                              {(quoteFields || [])
                                .filter((f) => f.kind === "input")
                                .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                                .flatMap((f) => [
                                  <SelectItem key={f.id} value={`quote::${f.id}::${f.label}`}>
                                    {f.label}
                                  </SelectItem>,
                                  <SelectItem key={`sum-quote-${f.id}`} value={`sum::quote::${f.id}::${f.label}`}>
                                    Σ All Prospects: {f.label}
                                  </SelectItem>,
                                ])}

                              <div className="px-2 py-1 text-xs text-slate-200/60">Data fields</div>
                              {(formFields || [])
                                .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
                                .flatMap((f) => [
                                  <SelectItem key={f.id} value={`data::${f.id}::${f.label}`}>
                                    {f.label}
                                  </SelectItem>,
                                  <SelectItem key={`sum-data-${f.id}`} value={`sum::data::${f.id}::${f.label}`}>
                                    Σ All Prospects: {f.label}
                                  </SelectItem>,
                                ])}

                              <div className="px-2 py-1 text-xs text-slate-200/60">Calculated fields</div>
                              {(quoteFields || [])
                                .filter((f) => f.kind === "calculated" && f.id !== formulaField?.id)
                                .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                                .map((f) => (
                                  <SelectItem key={f.id} value={`calculated::${f.id}::${f.label}`}>
                                    {f.label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <div className="text-sm text-slate-200/80">Number</div>
                          <div className="flex items-center gap-2">
                            <Input
                              value={formulaNumberDraft}
                              onChange={(e) => setFormulaNumberDraft(e.target.value)}
                              placeholder="e.g., 12"
                              className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10"
                            />
                          <Button
                              variant="secondary"
                              className="h-9 rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                              onClick={() => {
                                const raw = (formulaNumberDraft || "").trim();
                                if (!raw) return;
                                const n = Number(raw);
                                if (!Number.isFinite(n)) return;
                                addFormulaToken({ type: "number", value: n });
                                setFormulaNumberDraft("");
                              }}
                            >
                              Insert
                               </Button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm text-slate-200/80">Operators</div>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { t: "+", tok: { type: "op", op: "+" } },
                            { t: "-", tok: { type: "op", op: "-" } },
                            { t: "×", tok: { type: "op", op: "*" } },
                            { t: "÷", tok: { type: "op", op: "/" } },
                            { t: "(", tok: { type: "lparen" } },
                            { t: ")", tok: { type: "rparen" } },
                          ].map((b) => (
                          <Button
                              key={b.t}
                              variant="secondary"
                              className="h-9 w-11 rounded-xl bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                              onClick={() => addFormulaToken(b.tok)}
                            >
                              {b.t}
                               </Button>
                          ))}

                        <Button
                            variant="secondary"
                            className="h-9 rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                            onClick={removeLastFormulaToken}
                          >
                            Backspace
                             </Button>
                        <Button
                            variant="secondary"
                            className="h-9 rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                            onClick={() => setFormulaTokens([])}
                          >
                            Clear
                             </Button>
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                    <Button
                        variant="secondary"
                        className="rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                        onClick={() => setFormulaOpen(false)}
                      >
                        Cancel
                     </Button>
                    <Button
                        className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                        onClick={onSaveFormula}
                      >
                        Save
                     </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <div
                  className="rounded-xl border border-white/10 bg-white/5 p-4"
                  data-testid="quote-template-panel"
                >
                  <div className="text-sm font-semibold text-slate-100" data-testid="quote-template-title">
                    Quote Template
                  </div>
                  <div className="mt-1 text-xs text-slate-200/70" data-testid="quote-template-subtitle">
                    Apply a saved Quote Template to generate Quote Sheet inputs (Company and Product stay required).
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Select
                      value={quoteTemplateId}
                      onValueChange={setQuoteTemplateId}
                      disabled={!canEdit || quoteTemplateLoading}
                    >
                      <SelectTrigger
                        className="h-9 w-64 bg-white/5 text-slate-100 ring-1 ring-white/10"
                        data-testid="quote-template-select"
                      >
                        <SelectValue placeholder={quoteTemplateLoading ? "Loading..." : "Choose template"} />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        {(quoteTemplates || []).map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`quote-template-option-${t.id}`}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="secondary"
                      className="rounded-full bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
                      onClick={onApplyQuoteTemplate}
                      disabled={!canEdit || quoteTemplateApplying || !quoteTemplateId}
                      data-testid="quote-template-apply"
                    >
                      {quoteTemplateApplying ? "Applying…" : "Apply Template"}
                    </Button>
                  </div>
                </div>

                  <Tabs value={quoteTab} onValueChange={setQuoteTab}>
                    <TabsList className="bg-white/5 ring-1 ring-white/10">
                      <TabsTrigger
                        value="inputs"
                        className="data-[state=active]:bg-indigo-500/90 data-[state=active]:text-white data-[state=active]:hover:bg-indigo-500"
                      >
                        Input Fields
                      </TabsTrigger>
                      <TabsTrigger
                        value="calculated"
                        className="data-[state=active]:bg-indigo-500/90 data-[state=active]:text-white data-[state=active]:hover:bg-indigo-500"
                      >
                        Calculated Fields
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="inputs" className="mt-4 space-y-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="quote-sheet-input-config">
                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80">Field label</label>
                          <div className="flex items-center gap-2">
                            <Select value={quoteLabelMode} onValueChange={setQuoteLabelMode}>
                              <SelectTrigger className="h-9 w-[140px] bg-white/5 text-slate-100 ring-1 ring-white/10">
                                <SelectValue placeholder="Mode" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="preset">Preset</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                              </SelectContent>
                            </Select>

                            {quoteLabelMode === "preset" ? (
                              <Select value={quoteNewPresetLabel} onValueChange={setQuoteNewPresetLabel}>
                                <SelectTrigger className="h-9 w-full bg-white/5 text-slate-100 ring-1 ring-white/10">
                                  <SelectValue placeholder="Choose label" />
                                </SelectTrigger>
                                <SelectContent>
                                  {QUOTE_LABEL_PRESETS.map((l) => (
                                    <SelectItem key={l} value={l}>
                                      {l}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                value={quoteNewCustomLabel}
                                onChange={(e) => setQuoteNewCustomLabel(e.target.value)}
                                placeholder="Custom label"
                                className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10"
                              />
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80">Field type</label>
                          <Select
                            value={quoteNewFieldType}
                            onValueChange={(v) => {
                              setQuoteNewFieldType(v);
                              if (v !== "dropdown") setQuoteNewOptions("");
                            }}
                          >
                            <SelectTrigger className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10">
                              <SelectValue placeholder="Choose type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Text</SelectItem>
                              <SelectItem value="textarea">Long text</SelectItem>
                              <SelectItem value="dropdown">Drop down</SelectItem>
                              <SelectItem value="number">Number</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {quoteNewFieldType === "dropdown" ? (
                          <div className="space-y-2 md:col-span-2">
                            <label className="text-sm text-slate-200/80">Dropdown options (comma-separated)</label>
                            <Input
                              value={quoteNewOptions}
                              onChange={(e) => setQuoteNewOptions(e.target.value)}
                              placeholder="e.g., Term, Whole Life"
                              className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10"
                            />
                          </div>
                        ) : null}

                        {quoteNewFieldType === "number" ? (
                          <div className="flex items-center gap-3 md:col-span-2">
                            <Switch checked={quoteNewIncludeInHousehold} onCheckedChange={setQuoteNewIncludeInHousehold} />
                            <div className="text-sm text-slate-200/80">Include in Household sums</div>
                          </div>
                        ) : null}
                      </div>

                      <div className="space-y-2" data-testid="quote-sheet-input-list">
                        {(quoteFields || [])
                          .filter((f) => f.kind === "input")
                          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                          .map((f, idx, arr) => {
                            const isRequired = requiredQuoteFieldIds.has(f.id);

                            return (
                              <div
                                key={f.id}
                                className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                                data-testid={`quote-sheet-field-${f.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                      onClick={() => (canEdit && !isRequired ? onMoveQuoteField(f, -1) : null)}
                                      disabled={!canEdit || isRequired || idx === 0}
                                      title="Move up"
                                      data-testid={`quote-sheet-move-up-${f.id}`}
                                    >
                                      <ArrowUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                      onClick={() => (canEdit && !isRequired ? onMoveQuoteField(f, 1) : null)}
                                      disabled={!canEdit || isRequired || idx === arr.length - 1}
                                      title="Move down"
                                      data-testid={`quote-sheet-move-down-${f.id}`}
                                    >
                                      <ArrowDown className="h-4 w-4" />
                                    </Button>
                                  </div>

                                  <button
                                    type="button"
                                    className="text-left text-sm font-medium text-slate-100 hover:underline"
                                    onClick={() => (canEdit && !isRequired ? openEditQuoteField(f) : null)}
                                    data-testid={`quote-sheet-edit-${f.id}`}
                                  >
                                    {f.label}
                                  </button>
                                  <Badge className="bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/20">
                                    {quoteFieldTypeLabels[f.field_type] || f.field_type}
                                  </Badge>
                                  {isRequired ? (
                                    <Badge className="bg-emerald-500/15 text-emerald-100 ring-1 ring-emerald-400/20">Required</Badge>
                                  ) : null}
                                  {f.field_type === "number" && f.include_in_household ? (
                                    <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/10">Household</Badge>
                                  ) : null}
                                </div>

                                <div className="flex items-center gap-2">
                                  {f.field_type === "number" ? (
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={!!f.include_in_household}
                                        onCheckedChange={(v) => onUpdateQuoteField(f.id, { include_in_household: !!v })}
                                        disabled={!canEdit}
                                      />
                                      <span className="text-xs text-slate-200/70">Household</span>
                                    </div>
                                  ) : null}

                                  <Button
                                    variant="secondary"
                                    size="icon"
                                    className={
                                      "rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15 " +
                                      (canEdit ? "text-rose-300" : "text-rose-300/50")
                                    }
                                    onClick={() => (canEdit && !isRequired ? onDeleteQuoteField(f.id) : null)}
                                    disabled={!canEdit || isRequired}
                                    title={isRequired ? "Required" : "Delete field"}
                                    data-testid={`quote-sheet-delete-${f.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </TabsContent>

                    <TabsContent value="calculated" className="mt-4 space-y-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="quote-sheet-calculated-config">
                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80">Field label</label>
                          <div className="flex items-center gap-2">
                            <Select value={quoteLabelMode} onValueChange={setQuoteLabelMode}>
                              <SelectTrigger className="h-9 w-[140px] bg-white/5 text-slate-100 ring-1 ring-white/10">
                                <SelectValue placeholder="Mode" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="preset">Preset</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                              </SelectContent>
                            </Select>

                            {quoteLabelMode === "preset" ? (
                              <Select value={quoteNewPresetLabel} onValueChange={setQuoteNewPresetLabel}>
                                <SelectTrigger className="h-9 w-full bg-white/5 text-slate-100 ring-1 ring-white/10">
                                  <SelectValue placeholder="Choose label" />
                                </SelectTrigger>
                                <SelectContent>
                                  {QUOTE_LABEL_PRESETS.map((l) => (
                                    <SelectItem key={l} value={l}>
                                      {l}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                value={quoteNewCustomLabel}
                                onChange={(e) => setQuoteNewCustomLabel(e.target.value)}
                                placeholder="Custom label"
                                className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10"
                              />
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80">Field type</label>
                          <Select value={quoteNewFieldType} onValueChange={(v) => setQuoteNewFieldType(v)}>
                            <SelectTrigger className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10">
                              <SelectValue placeholder="Choose type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="number">Number</SelectItem>
                              <SelectItem value="text">Text</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2" data-testid="quote-sheet-calculated-list">
                        {(quoteFields || [])
                          .filter((f) => f.kind === "calculated")
                          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                          .map((f, idx, arr) => (
                            <div
                              key={f.id}
                              className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                 <Button
                                    variant="secondary"
                                    size="icon"
                                    className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                    onClick={() => (canEdit ? onMoveQuoteField(f, -1) : null)}
                                    disabled={!canEdit || idx === 0}
                                    title="Move up"
                                  >
                                    <ArrowUp className="h-4 w-4" />
                                    </Button>
                                 <Button
                                    variant="secondary"
                                    size="icon"
                                    className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                    onClick={() => (canEdit ? onMoveQuoteField(f, 1) : null)}
                                    disabled={!canEdit || idx === arr.length - 1}
                                    title="Move down"
                                  >
                                    <ArrowDown className="h-4 w-4" />
                                    </Button>
                                </div>

                                <button
                                  type="button"
                                  className="text-left text-sm font-medium text-slate-100 hover:underline"
                                  onClick={() => (canEdit ? openEditCalculatedField(f) : null)}
                                >
                                  {f.label}
                                </button>
                                <Badge className="bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/20">
                                  {quoteFieldTypeLabels[f.field_type] || f.field_type}
                                </Badge>
                                {f.show_in_household ? (
                                  <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/10">Household</Badge>
                                ) : null}
                                <div className="text-xs text-slate-200/60">
                                  {renderQuoteFormula(f.formula) ? `Formula: ${renderQuoteFormula(f.formula)}` : "Formula: (not set)"}
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={!!f.show_in_household}
                                    onCheckedChange={(v) => onUpdateQuoteField(f.id, { show_in_household: !!v })}
                                    disabled={!canEdit}
                                  />
                                  <span className="text-xs text-slate-200/70">Household</span>
                                </div>

                              <Button
                                  variant="secondary"
                                  size="icon"
                                  className="rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                  onClick={() => openFormulaEditor(f)}
                                  disabled={!canEdit}
                                  title="Edit formula"
                                >
                                  <Pencil className="h-4 w-4" />
                                   </Button>

                              <Button
                                  variant="secondary"
                                  size="icon"
                                  className={
                                    "rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15 " +
                                    (canEdit ? "text-rose-300" : "text-rose-300/50")
                                  }
                                  onClick={() => (canEdit ? onDeleteQuoteField(f.id) : null)}
                                  disabled={!canEdit}
                                  title="Delete field"
                                >
                                  <Trash2 className="h-4 w-4" />
                                   </Button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </TabsContent>
                  </Tabs>
              </CardContent>
              </Card>
            </div>
          ) : null}

          {/* Right: node editor */}
          {builderView === "node" ? (
            <div className="lg:col-span-8">
              
              <Card className="glass" data-testid="node-editor-card">
                {!canEdit ? (
                  <div
                    className="border-b border-white/10 bg-amber-500/10 px-6 py-3 text-sm text-amber-100"
                    data-testid="builder-readonly-banner"
                  >
                    Read-only mode: your role can run this script, but cannot edit it.
                  </div>
                ) : null}
                <CardHeader>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-slate-100" data-testid="node-editor-title">
                        <MessageSquareText className="h-5 w-5 text-indigo-400" />
                        Edit Node
                      </CardTitle>
                      <div className="mt-2 text-sm text-slate-200/70" data-testid="node-editor-subtitle">
                        Write what you read (your reply) and connect it to the next steps.
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2" data-testid="node-editor-actions">
                    <Button
                        variant="secondary"
                        className={
                          "rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15 " +
                          (canEdit ? "text-slate-100" : "text-slate-100/50")
                        }
                        onClick={onSetStart}
                        disabled={!active || !canEdit || isGreeting}
                        data-testid="set-start-node-button"
                      >
                        <Star className="mr-2 h-4 w-4" />
                        Start Node
                     </Button>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                          <Button
                              className={
                                "rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500 " +
                                (!canEdit ? "opacity-60" : "")
                              }
                              onClick={onSaveNode}
                              disabled={!active || !canEdit}
                              data-testid="save-node-button"
                            >
                              <Save className="h-4 w-4" />
                               </Button>

                          </TooltipTrigger>
                          <TooltipContent side="bottom">Save</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                          <Button
                              variant="secondary"
                              size="icon"
                              className={
                                "h-10 w-10 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15 " +
                                (canEdit ? "text-rose-300" : "text-rose-300/50")
                              }
                              onClick={onDeleteNode}
                              disabled={!active || isStart || isGreeting || !canEdit}
                              data-testid="delete-node-button"
                              title="Delete node"
                            >
                              <Trash2 className="h-4 w-4" />
                               </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            {isStart
                              ? "Start node can't be deleted"
                              : isGreeting
                                ? "Greeting node can't be deleted"
                                : "Delete node"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-5">
              {!active ? (
                <div className="muted" data-testid="no-node-selected-text">
                  Select a node on the left.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2" data-testid="node-title-field">
                      <label className="text-sm text-slate-200/80" data-testid="node-title-label">
                        Title (optional)
                      </label>
                      <Input
                        value={nodeTitle}
                        onChange={(e) => setNodeTitle(e.target.value)}
                        placeholder="Price objection"
                        className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                        data-testid="node-title-input"
                      />

                  <div className="space-y-2" data-testid="node-scope-field">
                    <label className="text-sm text-slate-200/80" data-testid="node-scope-label">
                      Node Scope
                    </label>
                    <Select value={nodeScope} onValueChange={setNodeScope}>
                      <SelectTrigger className="h-10 bg-white/5 text-slate-100 ring-1 ring-white/10" data-testid="node-scope-select">
                        <SelectValue placeholder="Choose scope" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="plan_options">Plan Options</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                    </div>

                    {/* Node ID removed per design */}
                  </div>

                  <div style={{ display: "none" }} data-testid="legacy-builder-sections-removed" />

                  <Collapsible defaultOpen={false} data-testid="coaching-note-collapsible">
                    <CollapsibleTrigger
                      className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm font-medium text-slate-100 hover:bg-white/10"
                      data-testid="coaching-note-toggle"
                    >
                      Coaching note (internal)
                      <ChevronDown className="h-4 w-4 text-slate-200/70" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2" data-testid="coaching-note-content">
                      <Textarea
                        value={coachingNote}
                        onChange={(e) => setCoachingNote(e.target.value)}
                        placeholder="Tone, pacing, reminders, etc. Not shown in Runner unless you open it."
                        className="min-h-[120px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                        data-testid="coaching-note-input"
                      />
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator className="bg-white/10" />

                  {/* Script Blocks Section (NEW) */}
                  <div className="space-y-3" data-testid="script-blocks-section">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-base font-semibold text-slate-100">
                          <FileText className="h-4 w-4 text-indigo-400" />
                          Script Blocks
                        </div>
                        <div className="mt-2 text-sm text-slate-200/70">
                          Build the script using ordered Script Blocks. Each Script Block can contain text blocks and data fields.
                        </div>
                      </div>

                      <Button
                        className={
                          "rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500 " +
                          (!canEdit ? "opacity-60" : "")
                        }
                        disabled={!canEdit}
                        onClick={() =>
                          setScriptBlocks((prev) => [
                            ...prev,
                            {
                              id: crypto.randomUUID(),
                              scope: "household",
                              blocks: [{ id: crypto.randomUUID(), type: "text", text: "" }],
                              __seededFromLegacy: true,
                            },
                          ])
                        }
                        data-testid="add-script-block"
                      >
                        + Script Block
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {(scriptBlocks || []).map((sb, sbIdx) => (
                        <div
                          key={sb.id}
                          className="rounded-2xl border border-white/10 bg-white/5 p-3"
                          data-testid={`script-block-${sbIdx}`}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div className="text-sm font-medium text-slate-100">Script Block</div>

                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-200/70">Scope:</span>
                              <Select
                                value={sb.scope || "household"}
                                onValueChange={(val) =>
                                  setScriptBlocks((prev) =>
                                    prev.map((x) =>
                                      x.id === sb.id ? { ...x, scope: val } : x,
                                    ),
                                  )
                                }
                              >
                                <SelectTrigger className="h-8 w-[160px] rounded-full bg-white/5 text-slate-100 ring-1 ring-white/10">
                                  <SelectValue placeholder="Scope" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="household">Household</SelectItem>
                                  <SelectItem value="prospect">Prospect</SelectItem>
                                </SelectContent>
                              </Select>

                              <Button
                                variant="secondary"
                                size="icon"
                                className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                onClick={() => {
                                  if (sbIdx === 0) return;
                                  setScriptBlocks((prev) => moveItem(prev, sbIdx, sbIdx - 1));
                                }}
                                disabled={!canEdit || sbIdx === 0}
                                data-testid={`script-block-move-up-${sbIdx}`}
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="secondary"
                                size="icon"
                                className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                onClick={() => {
                                  if (sbIdx === (scriptBlocks || []).length - 1) return;
                                  setScriptBlocks((prev) => moveItem(prev, sbIdx, sbIdx + 1));
                                }}
                                disabled={!canEdit || sbIdx === (scriptBlocks || []).length - 1}
                                data-testid={`script-block-move-down-${sbIdx}`}
                              >
                                <ChevronDownIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="secondary"
                                size="icon"
                                className="h-8 w-8 rounded-full bg-white/10 text-rose-300 ring-1 ring-white/10 hover:bg-white/15"
                                onClick={() =>
                                  setScriptBlocks((prev) => prev.filter((x) => x.id !== sb.id))
                                }
                                disabled={!canEdit}
                                data-testid={`script-block-delete-${sbIdx}`}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="mb-2 flex items-center gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              className="rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                              onClick={() =>
                                setScriptBlocks((prev) =>
                                  prev.map((x) =>
                                    x.id === sb.id
                                      ? {
                                          ...x,
                                          blocks: [
                                            ...(x.blocks || []),
                                            { id: crypto.randomUUID(), type: "text", text: "" },
                                          ],
                                        }
                                      : x,
                                  ),
                                )
                              }
                              disabled={!canEdit}
                              data-testid={`script-block-${sbIdx}-add-text`}
                            >
                              + Text
                            </Button>

                            <Select
                              value=""
                              onValueChange={(val) => {
                                const [kind, id, ...rest] = String(val || "").split("::");
                                const label = rest.join("::");
                                if (kind !== "data_field" || !id) return;
                                setScriptBlocks((prev) =>
                                  prev.map((x) =>
                                    x.id === sb.id
                                      ? {
                                          ...x,
                                          blocks: [
                                            ...(x.blocks || []),
                                            {
                                              id: crypto.randomUUID(),
                                              type: "field",
                                              source: "data",
                                              field_id: id,
                                              label,
                                              prospect_scope:
                                                (sb.scope || "household") === "prospect"
                                                  ? "prospect_1"
                                                  : "household",
                                              option_index: 1,
                                            },
                                          ],
                                        }
                                      : x,
                                  ),
                                );
                              }}
                            >
                              <SelectTrigger className="h-8 w-[220px] rounded-full bg-white/5 text-slate-100 ring-1 ring-white/10">
                                <SelectValue placeholder="+ Data Field" />
                              </SelectTrigger>
                              <SelectContent>
                                <div className="p-2">
                                  <Input
                                    value={dataFieldSearch}
                                    onChange={(event) => setDataFieldSearch(event.target.value)}
                                    placeholder="Search data fields"
                                    className="h-8 bg-white/5 text-slate-100 ring-1 ring-white/10"
                                    data-testid="script-data-field-search-input"
                                  />
                                </div>
                                {dataFieldOptions.map((f) => (
                                  <SelectItem
                                    key={f.id}
                                    value={`data_field::${f.id}::${f.label}`}
                                  >
                                    {f.label}
                                  </SelectItem>
                                ))}
                                {!dataFieldOptions.length ? (
                                  <div
                                    className="px-2 py-2 text-xs text-slate-200/60"
                                    data-testid="script-data-field-search-empty"
                                  >
                                    No matching data fields.
                                  </div>
                                ) : null}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-3">
                            {(sb.blocks || []).map((b, idx) => (
                              <div
                                key={b.id}
                                className="rounded-xl border border-white/10 bg-white/5 p-3"
                                data-testid={`script-block-${sbIdx}-child-${idx}`}
                              >
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <div className="text-sm text-slate-100">
                                    {b.type === "text"
                                      ? "Text"
                                      : b.type === "field"
                                        ? `Data Field: ${b.label || ""}`
                                        : "Block"}
                                  </div>

                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                      onClick={() =>
                                        setScriptBlocks((prev) =>
                                          prev.map((x) =>
                                            x.id === sb.id
                                              ? {
                                                  ...x,
                                                  blocks: moveItem(
                                                    x.blocks || [],
                                                    idx,
                                                    idx - 1,
                                                  ),
                                                }
                                              : x,
                                          ),
                                        )
                                      }
                                      disabled={!canEdit || idx === 0}
                                      data-testid={`script-block-${sbIdx}-child-move-up-${idx}`}
                                    >
                                      <ChevronUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                      onClick={() =>
                                        setScriptBlocks((prev) =>
                                          prev.map((x) =>
                                            x.id === sb.id
                                              ? {
                                                  ...x,
                                                  blocks: moveItem(
                                                    x.blocks || [],
                                                    idx,
                                                    idx + 1,
                                                  ),
                                                }
                                              : x,
                                          ),
                                        )
                                      }
                                      disabled={!canEdit || idx === (sb.blocks || []).length - 1}
                                      data-testid={`script-block-${sbIdx}-child-move-down-${idx}`}
                                    >
                                      <ChevronDownIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-8 w-8 rounded-full bg-white/10 text-rose-300 ring-1 ring-white/10 hover:bg-white/15"
                                      onClick={() =>
                                        setScriptBlocks((prev) =>
                                          prev.map((x) =>
                                            x.id === sb.id
                                              ? {
                                                  ...x,
                                                  blocks: (x.blocks || []).filter(
                                                    (z) => z.id !== b.id,
                                                  ),
                                                }
                                              : x,
                                          ),
                                        )
                                      }
                                      disabled={!canEdit}
                                      data-testid={`script-block-${sbIdx}-child-delete-${idx}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>

                                {b.type === "text" ? (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-slate-200/70">Insert:</span>
                                      <Select
                                        onValueChange={(val) => {
                                          const parts = String(val || "").split("::");
                                          const kind = parts[0];
                                          const id = parts[1];
                                          if (!kind || !id) return;

                                          const token =
                                            kind === "data_var"
                                              ? `$(data:${id})`
                                              : kind === "quote_var"
                                                ? `$(quote:${id})`
                                                : kind === "calc_var"
                                                  ? `$(calc:${id})`
                                                  : kind === "agent_var"
                                                    ? `$(agent:${id})`
                                                    : kind === "customer_var"
                                                      ? `$(customer:${id})`
                                                    : kind === "option_quote_var"
                                                      ? `$(option:${id}:${parts[2]})`
                                                      : "";
                                          if (!token) return;
                                          insertTokenIntoTextBlock(b.id, token);
                                        }}
                                      >
                                        <SelectTrigger className="h-8 w-[240px] rounded-full bg-white/5 text-slate-100 ring-1 ring-white/10">
                                          <SelectValue placeholder="Variable Token" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <div className="p-2">
                                            <Input
                                              value={tokenSearch}
                                              onChange={(event) => setTokenSearch(event.target.value)}
                                              placeholder="Search tokens"
                                              className="h-8 bg-white/5 text-slate-100 ring-1 ring-white/10"
                                              data-testid="script-token-search-input"
                                            />
                                          </div>

                                          {tokenDataFields.length ? (
                                            <div className="px-2 py-1 text-xs text-slate-200/60">Data Fields</div>
                                          ) : null}
                                          {tokenDataFields.map((f) => (
                                            <SelectItem key={f.id} value={`data_var::${f.id}`}>
                                              {f.label}
                                            </SelectItem>
                                          ))}

                                          {tokenQuoteInputs.length ? (
                                            <div className="px-2 py-1 text-xs text-slate-200/60">Quote Inputs</div>
                                          ) : null}
                                          {tokenQuoteInputs.map((f) => (
                                            <SelectItem key={f.id} value={`quote_var::${f.id}`}>
                                              {f.label}
                                            </SelectItem>
                                          ))}

                                          {tokenQuoteOptions.length ? (
                                            <div className="px-2 py-1 text-xs text-slate-200/60">Quote Inputs (Plan Options)</div>
                                          ) : null}
                                          {tokenQuoteOptions.map((optItem) => (
                                            <SelectItem
                                              key={`opt${optItem.opt}-${optItem.field.id}`}
                                              value={`option_quote_var::${optItem.opt}::${optItem.field.id}`}
                                            >
                                              {optItem.label}
                                            </SelectItem>
                                          ))}

                                          {tokenAgentOptions.length ? (
                                            <div className="px-2 py-1 text-xs text-slate-200/60">Agent</div>
                                          ) : null}
                                          {tokenAgentOptions.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                              {item.label}
                                            </SelectItem>
                                          ))}

                                          {/* AW-2C-005. Its own group,
                                              directly below Agent, so the
                                              distinction an author has to get
                                              right is visible at the moment
                                              they choose between them. */}
                                          {tokenCustomerOptions.length ? (
                                            <div className="px-2 py-1 text-xs text-slate-200/60">Customer</div>
                                          ) : null}
                                          {tokenCustomerOptions.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                              {item.label}
                                            </SelectItem>
                                          ))}

                                          {tokenQuoteCalculated.length ? (
                                            <div className="px-2 py-1 text-xs text-slate-200/60">Quote Calculated</div>
                                          ) : null}
                                          {tokenQuoteCalculated.map((f) => (
                                            <SelectItem key={f.id} value={`calc_var::${f.id}`}>
                                              {f.label}
                                            </SelectItem>
                                          ))}

                                          {!hasTokenMatches ? (
                                            <div
                                              className="px-2 py-2 text-xs text-slate-200/60"
                                              data-testid="script-token-search-empty"
                                            >
                                              No matches found.
                                            </div>
                                          ) : null}

                                        </SelectContent>
                                      </Select>
                                    </div>

                                    <Textarea
                                      ref={(el) => {
                                        if (el) textAreaRefs.current[b.id] = el;
                                      }}
                                      value={b.text || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setScriptBlocks((prev) =>
                                          prev.map((x) =>
                                            x.id === sb.id
                                              ? {
                                                  ...x,
                                                  blocks: (x.blocks || []).map((z) =>
                                                    z.id === b.id ? { ...z, text: val } : z,
                                                  ),
                                                }
                                              : x,
                                          ),
                                        );
                                      }}
                                      placeholder="Write your exact line here"
                                      className="min-h-[120px] bg-white/0 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                                      data-testid={`script-block-${sbIdx}-child-text-${idx}`}
                                    />
                                  </div>
                                ) : (
                                  <div className="text-sm text-slate-200/70">Will render in Runner</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>


                  {/* Data Collection Fields Section */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-base font-semibold text-slate-100" data-testid="data-fields-title">
                        <FileText className="h-4 w-4 text-indigo-400" />
                        Data Fields
                      </div>
                      <div className="mt-2 text-sm text-slate-200/70" data-testid="data-fields-subtitle">
                        Capture prospect info when agent reaches this node.
                      </div>
                    </div>

                    <Dialog open={addFieldOpen} onOpenChange={setAddFieldOpen}>
                      <DialogTrigger asChild>
                      <Button
                          className={
                            "rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500 " +
                            (!canEdit ? "opacity-60" : "")
                          }
                          disabled={!canEdit}
                          data-testid="add-field-open-button"
                        >
                          <Plus className="h-4 w-4" />
                           </Button>
                      </DialogTrigger>
                      <DialogContent className="glass border-white/10" data-testid="add-field-dialog">
                        <DialogHeader>
                          <DialogTitle className="text-slate-100" data-testid="add-field-title">
                            Add data field
                          </DialogTitle>
                          <DialogDescription className="text-slate-200/70" data-testid="add-field-description">
                            Agents will see this field when they reach this node during a call.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm text-slate-200/80" data-testid="add-field-label-label">
                              Field label
                            </label>
                            <Input
                              value={newFieldLabel}
                              onChange={(e) => setNewFieldLabel(e.target.value)}
                              placeholder="e.g., Phone Number, Email, Notes"
                              className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                              data-testid="add-field-label-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm text-slate-200/80" data-testid="add-field-scope-label">
                              Scope
                            </label>
                            <Select value={newFieldScope} onValueChange={setNewFieldScope}>
                              <SelectTrigger
                                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                                data-testid="add-field-scope-select"
                              >
                                <SelectValue placeholder="Choose scope" />
                              </SelectTrigger>
                              <SelectContent data-testid="add-field-scope-select-content">
                                <SelectItem value="household" data-testid="add-field-scope-household">
                                  Household
                                </SelectItem>
                                <SelectItem value="prospects" data-testid="add-field-scope-prospects">
                                  Prospects
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm text-slate-200/80" data-testid="add-field-type-label">
                              Field type
                            </label>
                            <Select
                              value={newFieldType}
                              onValueChange={(val) => {
                                setNewFieldType(val);
                                if (val !== "dropdown") setNewFieldOptions("");
                              }}
                            >
                              <SelectTrigger
                                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                                data-testid="add-field-type-select"
                              >
                                <SelectValue placeholder="Choose type" />
                              </SelectTrigger>
                              <SelectContent data-testid="add-field-type-select-content">
                                <SelectItem value="text" data-testid="add-field-type-text">
                                  Text
                                </SelectItem>
                                <SelectItem value="phone" data-testid="add-field-type-phone">
                                  Phone
                                </SelectItem>
                                <SelectItem value="email" data-testid="add-field-type-email">
                                  Email
                                </SelectItem>
                                <SelectItem value="textarea" data-testid="add-field-type-textarea">
                                  Long text
                                </SelectItem>
                                <SelectItem value="checkbox" data-testid="add-field-type-checkbox">
                                  Checkbox
                                </SelectItem>
                                <SelectItem value="dropdown" data-testid="add-field-type-dropdown">
                                  Dropdown
                                </SelectItem>
                                <SelectItem value="number" data-testid="add-field-type-number">
                                  Number
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Dropdown Options Input */}
                          {newFieldType === "dropdown" && (
                            <div className="space-y-2">
                              <label className="text-sm text-slate-200/80" data-testid="add-field-options-label">
                                Dropdown options (comma-separated)
                              </label>
                              <Input
                                value={newFieldOptions}
                                onChange={(e) => setNewFieldOptions(e.target.value)}
                                placeholder="e.g., Option 1, Option 2, Option 3"
                                className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                                data-testid="add-field-options-input"
                              />
                              <p className="text-xs text-slate-400">
                                Enter each option separated by a comma
                              </p>
                            </div>
                          )}

                          <div className="flex items-center gap-3">
                            <Switch
                              checked={newFieldRequired}
                              onCheckedChange={setNewFieldRequired}
                              data-testid="add-field-required-switch"
                            />
                            <label className="text-sm text-slate-200/80" data-testid="add-field-required-label">
                              Required field
                            </label>
                          </div>
                        </div>


 

                        <DialogFooter>
                        <Button
                            className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                            onClick={onAddField}
                            disabled={
                              !newFieldLabel.trim() ||
                              (newFieldType === "dropdown" && !newFieldOptions.trim())
                            }
                            data-testid="add-field-submit-button"
                          >
                            Add field
                             </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>


                  {/* Edit Field Dialog (opened via pencil icon in field rows) */}
                  <Dialog open={editFieldOpen} onOpenChange={setEditFieldOpen}>
                    <DialogContent className="glass border-white/10" data-testid="edit-field-dialog">
                      <DialogHeader>
                        <DialogTitle className="text-slate-100" data-testid="edit-field-title">
                          Edit data field
                        </DialogTitle>
                        <DialogDescription className="text-slate-200/70" data-testid="edit-field-description">
                          Changes apply everywhere this field is used, including prior runs (labels update via field definition).
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80" data-testid="edit-field-label-label">
                            Field label
                          </label>
                          <Input
                            value={editFieldLabel}
                            onChange={(e) => setEditFieldLabel(e.target.value)}
                            placeholder="e.g., Phone Number, Email, Notes"
                            className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                            data-testid="edit-field-label-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80" data-testid="edit-field-scope-label">
                            Scope
                          </label>
                          <Select value={editFieldScope} onValueChange={setEditFieldScope}>
                            <SelectTrigger
                              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                              data-testid="edit-field-scope-select"
                            >
                              <SelectValue placeholder="Choose scope" />
                            </SelectTrigger>
                            <SelectContent data-testid="edit-field-scope-select-content">
                              <SelectItem value="household" data-testid="edit-field-scope-household">
                                Household
                              </SelectItem>
                              <SelectItem value="prospects" data-testid="edit-field-scope-prospects">
                                Prospects
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>


                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80" data-testid="edit-field-type-label">
                            Field type
                          </label>
                          <Select
                            value={editFieldType}
                            onValueChange={(val) => {
                              setEditFieldType(val);
                              if (val !== "dropdown") setEditFieldOptions("");
                            }}
                          >
                            <SelectTrigger
                              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                              data-testid="edit-field-type-select"
                            >
                              <SelectValue placeholder="Choose type" />
                            </SelectTrigger>
                            <SelectContent data-testid="edit-field-type-select-content">
                              <SelectItem value="text" data-testid="edit-field-type-text">Text</SelectItem>
                              <SelectItem value="phone" data-testid="edit-field-type-phone">Phone</SelectItem>
                              <SelectItem value="email" data-testid="edit-field-type-email">Email</SelectItem>
                              <SelectItem value="textarea" data-testid="edit-field-type-textarea">Long text</SelectItem>
                              <SelectItem value="checkbox" data-testid="edit-field-type-checkbox">Checkbox</SelectItem>
                              <SelectItem value="dropdown" data-testid="edit-field-type-dropdown">Dropdown</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {editFieldType === "dropdown" && (
                          <div className="space-y-2">
                            <label className="text-sm text-slate-200/80" data-testid="edit-field-options-label">
                              Dropdown options (comma-separated)
                            </label>
                            <Input
                              value={editFieldOptions}
                              onChange={(e) => setEditFieldOptions(e.target.value)}
                              placeholder="e.g., Option 1, Option 2, Option 3"
                              className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                              data-testid="edit-field-options-input"
                            />
                            <p className="text-xs text-slate-400">
                              Existing run values will be kept as-is even if they do not match the current options.
                            </p>
                          </div>
                        )}

                        <div className="flex items-center gap-3">
                          <Switch
                            checked={editFieldRequired}
                            onCheckedChange={setEditFieldRequired}
                            data-testid="edit-field-required-switch"
                          />
                          <label className="text-sm text-slate-200/80" data-testid="edit-field-required-label">
                            Required field
                          </label>
                        </div>
                      </div>

                      <DialogFooter>
                      <Button
                          className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                          onClick={onSaveEditField}
                          disabled={!editFieldLabel.trim() || (editFieldType === "dropdown" && !editFieldOptions.trim())}
                          data-testid="edit-field-save-button"
                        >
                          Save changes
                           </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {activeNodeFields.length === 0 ? (
                    <div className="muted" data-testid="no-fields-text">
                      No data fields on this node. Add fields to capture prospect info.
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="fields-list">
                      {[...activeNodeFields]
                        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                        .map((f, idx, arr) => (
                          <div
                            key={f.id}
                            className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                            data-testid={`field-row-${f.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                              <Button
                                  variant="secondary"
                                  size="icon"
                                  className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                  onClick={() => (canEdit ? onMoveField(f, -1) : null)}
                                  disabled={!canEdit || idx === 0}
                                  data-testid={`field-move-up-${f.id}`}
                                  title="Move up"
                                >
                                  <ArrowUp className="h-4 w-4" />
                                   </Button>
                              <Button
                                  variant="secondary"
                                  size="icon"
                                  className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                  onClick={() => (canEdit ? onMoveField(f, 1) : null)}
                                  disabled={!canEdit || idx === arr.length - 1}
                                  data-testid={`field-move-down-${f.id}`}
                                  title="Move down"
                                >
                                  <ArrowDown className="h-4 w-4" />
                                   </Button>
                              </div>

                              <div className="text-sm font-medium text-slate-100" data-testid={`field-label-${f.id}`}>
                                {f.label}
                              </div>
                              <Badge
                                className="bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/20"
                                data-testid={`field-type-badge-${f.id}`}
                              >
                                {fieldTypeLabels[f.field_type] || f.field_type}
                              </Badge>
                              {f.required && (
                                <Badge
                                  className="bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/20"
                                  data-testid={`field-required-badge-${f.id}`}
                                >
                                  Required
                                </Badge>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                            <Button
                                variant="secondary"
                                size="icon"
                                className="rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15"
                                onClick={() => (canEdit ? openEditField(f) : null)}
                                disabled={!canEdit}
                                data-testid={`edit-field-button-${f.id}`}
                                title="Edit field"
                              >
                                <Pencil className="h-4 w-4" />
                                 </Button>

                            <Button
                                variant="secondary"
                                size="icon"
                                className={
                                  "rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15 " +
                                  (canEdit ? "text-rose-300" : "text-rose-300/50")
                                }
                                onClick={() => (canEdit ? onDeleteField(f.id) : null)}
                                disabled={!canEdit}
                                data-testid={`delete-field-button-${f.id}`}
                                title="Delete field"
                              >
                                <Trash2 className="h-4 w-4" />
                                 </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

 
 

 

 



                  <Separator className="bg-white/10" />

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-base font-semibold text-slate-100" data-testid="options-title">
                        <User className="h-4 w-4 text-indigo-400" />
                        Prospect Responses
                      </div>
                      <div className="mt-2 text-sm text-slate-200/70" data-testid="options-subtitle">
                        Each response becomes a button in the Runner and routes to the next node.
                      </div>
                    </div>

                    <Dialog open={addOptionOpen} onOpenChange={setAddOptionOpen}>
                      <DialogTrigger asChild>
                      <Button
                          className={
                            "rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500 " +
                            (!canEdit ? "opacity-60" : "")
                          }
                          disabled={!canEdit}
                          data-testid="add-option-open-button"
                        >
                          <Plus className="h-4 w-4" />
                           </Button>
                      </DialogTrigger>
                      <DialogContent className="glass border-white/10" data-testid="add-option-dialog">
                        <DialogHeader>
                          <DialogTitle className="text-slate-100" data-testid="add-option-title">
                            Add prospect response
                          </DialogTitle>
                          <DialogDescription className="text-slate-200/70" data-testid="add-option-description">
                            Example: “Not interested” or “How much does it cost?”
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm text-slate-200/80" data-testid="add-option-prospect-label">
                              Prospect says (button label)
                            </label>
                            <Input
                              value={optProspectSays}
                              onChange={(e) => setOptProspectSays(e.target.value)}
                              placeholder="I’m not interested"
                              className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                              data-testid="add-option-prospect-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm text-slate-200/80" data-testid="add-option-target-label">
                              Next node
                            </label>
                            <Select value={optTargetId} onValueChange={setOptTargetId}>
                              <SelectTrigger
                                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                                data-testid="add-option-target-select"
                              >
                                <SelectValue placeholder="Choose a node" />
                              </SelectTrigger>
                              <SelectContent data-testid="add-option-target-select-content">
                                {orderedNodesList.map((n) => (
                                  <SelectItem
                                    key={n.node.id}
                                    value={n.node.id}
                                    data-testid={`add-option-target-item-${n.node.id}`}
                                  >
                                    <span className="text-xs text-slate-200/60">
                                      #{nodePositionById[n.node.id] || "—"}
                                    </span>
                                    <span className="ml-2">{nodeLabel(n.node)}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <DialogFooter>
                        <Button
                            className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                            onClick={onAddOption}
                            disabled={!optProspectSays.trim() || !optTargetId}
                            data-testid="add-option-submit-button"
                          >
                            Add
                             </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {active.options.length === 0 ? (
                    <div className="muted" data-testid="no-options-text">
                      No prospect responses yet. Add at least one to create your branching.
                    </div>
                  ) : (
                    <ProspectResponsesList
                      options={active.options}
                      nodes={orderedNodesList}
                      nodePositionById={nodePositionById}
                      canEdit={canEdit}
                      onUpdateOption={onUpdateOption}
                      onDeleteOption={onDeleteOption}
                    />
                  )}
                  </>
              )}
            </CardContent>
          </Card>
            </div>
        ) : null}
        </div>
      )}
    </div>
  );
}
